import React, { useState, useEffect } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
  TablePagination, Paper, IconButton, Box, Button, TextField, Dialog, DialogActions, 
  DialogContent, DialogTitle, CircularProgress, Autocomplete, InputAdornment, MenuItem
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from "react-router-dom";
import Navbar from "../../../../globals/components/Navbar";
import { apiClient } from "../../../../globals/utils/api";
import { toast } from 'react-toastify';
import TablePaginationActions from "../../../../globals/components/TablePaginationActions";

function StateList() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [openCountyDialog, setOpenCountyDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedState, setSelectedState] = useState(null);
  const [newState, setNewState] = useState("");
  const [newStateCode, setNewStateCode] = useState("");
  const [newCarrier, setNewCarrier] = useState(null);
  const [newManagedBy, setNewManagedBy] = useState(null);
  const [goesToCountyLevel, setGoesToCountyLevel] = useState("No");
  const [selectedCountyState, setSelectedCountyState] = useState(null);
  const [newCounty, setNewCounty] = useState("");
  const [countyCarrier, setCountyCarrier] = useState(null); // Default carrier for county dialog
  const [carriers, setCarriers] = useState([]);
  const [users, setUsers] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchDropdownData = async () => {
      setLoading(true);
      try {
        const carrierRes = await apiClient(`/carriers/0/`, "GET");
        setCarriers(carrierRes?.data || []);
  
        const userRes = await apiClient(`/internal-user`, "GET");
        setUsers(userRes?.data || []);
  
        const statesRes = await apiClient(`/states/0/`, "GET");  
        setStates(Array.isArray(statesRes) ? statesRes : statesRes.data || []);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load data.");
      } finally {
        setLoading(false);
      }
    };
    fetchDropdownData();
  }, []);

  const refreshStatesList = async () => {
    const statesRes = await apiClient(`/states/0/`, "GET");
    setStates(Array.isArray(statesRes) ? statesRes : statesRes.data || []);
  };

  const handleSaveState = async () => {
    setSubmitting(true);
    try {
      const stateData = {
        state_name: newState,
        state_code: newStateCode,
        default_carrier_id: newCarrier?.bond_carrier_id || null,
        managed_by: newManagedBy?.internal_user_id || null,
        is_county_level: goesToCountyLevel === "Yes",
      };
      if (editMode) {
        await apiClient(`/states/${selectedState.bond_state_id}/`, "PATCH", stateData);
        toast.success("State updated successfully");
      } else {
        await apiClient(`/states/`, "POST", stateData);
        toast.success("State added successfully");
      }
      handleCloseDialog();
      await refreshStatesList();
    } catch (error) {
      console.error("Error saving state:", error);
      toast.error("Failed to save state.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveCounty = async () => {
    setSubmitting(true);
    try {
      const countyData = {
        bond_county: newCounty,
        bond_state_id: selectedCountyState?.bond_state_id || null,
        default_carrier_id: countyCarrier?.bond_carrier_id || null,
      };
      await apiClient(`/counties/create/`, "POST", countyData);
      toast.success("County added successfully");
      handleCloseCountyDialog();
    } catch (error) {
      console.error("Error saving county:", error);
      toast.error("Failed to save county.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenDialog = (state = null) => {
    if (state) {
      setEditMode(true);
      setSelectedState(state);
      setNewState(state.state_name);
      setNewStateCode(state.state_code);
  
      // Try to find carrier by name as fallback (assuming 'default_carrier' is name here)
      const selectedCarrier = carriers.find((carrier) => carrier.name === state.default_carrier) || null;
      const selectedUser = users.find((user) => user.user.username === state.managed_by_name) || null;
  
      setNewCarrier(selectedCarrier);
      setNewManagedBy(selectedUser);
      setGoesToCountyLevel(state.is_county_level ? "Yes" : "No");
    } else {
      setEditMode(false);
      setNewState("");
      setNewStateCode("");
      setNewCarrier(null);
      setNewManagedBy(null);
      setGoesToCountyLevel("No");
    }
    setOpenDialog(true);
  };  

  const handleOpenCountyDialog = () => {
    setOpenCountyDialog(true);
    setSelectedCountyState(null);
    setNewCounty("");
    setCountyCarrier(null); // Reset carrier for new county dialog
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedState(null);
  };

  const handleCloseCountyDialog = () => {
    setOpenCountyDialog(false);
  };

  const filteredStates = states.filter((state) =>
    state.state_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const currentRows = filteredStates.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <>
      <Navbar />
      <Box sx={{ maxWidth: "100%", padding: "20px" }}>
        <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2 }}>States</Typography>
        <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: "20px" }}>
          <TextField
            label="Search by State Name"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
              New State
            </Button>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleOpenCountyDialog}>
              New County
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: "200px" }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ width: "100%", overflowX: "auto", mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow style={{ backgroundColor: "#f0f0f0" }}>
                  <TableCell>State</TableCell>
                  <TableCell>State Code</TableCell>
                  <TableCell>Default Carrier</TableCell>
                  <TableCell>Managed By</TableCell>
                  <TableCell>Goes to County Level</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentRows.map((state, index) => (
                  <TableRow key={index}>
                    <TableCell>{state.state_name}</TableCell>
                    <TableCell>{state.state_code}</TableCell>
                    <TableCell>{state.default_carrier}</TableCell>
                    <TableCell>{state.managed_by_name}</TableCell>
                    <TableCell>{state.is_county_level ? "Yes" : "No"}</TableCell>
                    <TableCell>
                      <IconButton color="primary" onClick={() => handleOpenDialog(state)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton color="primary" onClick={() => {localStorage.setItem("state_name", state.state_name); navigate(`/state-details/${state.bond_state_id}`);}}>
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TablePagination
          ActionsComponent={TablePaginationActions}
          component="div"
          count={filteredStates.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
        />
      </Box>

      {/* Dialog for Adding or Editing State */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{editMode ? "Edit State" : "Add New State"}</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label="State Name" fullWidth variant="outlined" value={newState} onChange={(e) => setNewState(e.target.value)} />
          <TextField margin="dense" label="State Code" fullWidth variant="outlined" value={newStateCode} onChange={(e) => setNewStateCode(e.target.value)} />
          <Autocomplete
            options={carriers}
            getOptionLabel={(carrier) => carrier.name || ""}
            value={newCarrier || null}
            onChange={(e, selectedCarrier) => setNewCarrier(selectedCarrier || null)}
            isOptionEqualToValue={(option, value) => option.bond_carrier_id === value.bond_carrier_id}
            renderInput={(params) => <TextField {...params} label="Default Carrier" margin="dense" variant="outlined" fullWidth />}
          />
          <Autocomplete
            options={users}
            getOptionLabel={(user) => user.user.username || ""}
            value={newManagedBy || null}
            onChange={(e, selectedUser) => setNewManagedBy(selectedUser || null)}
            isOptionEqualToValue={(option, value) => option.internal_user_id === value.internal_user_id}
            renderInput={(params) => <TextField {...params} label="Managed By" margin="dense" variant="outlined" fullWidth />}
          />
          <TextField select margin="dense" label="Goes to County Level" fullWidth variant="outlined" value={goesToCountyLevel} onChange={(e) => setGoesToCountyLevel(e.target.value)}>
            <MenuItem value="Yes">Yes</MenuItem>
            <MenuItem value="No">No</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">Cancel</Button>
          <Button onClick={handleSaveState} color="primary" disabled={submitting}>
            {submitting ? <CircularProgress size={20} /> : editMode ? "Save" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for Adding New County */}
      <Dialog open={openCountyDialog} onClose={handleCloseCountyDialog}>
        <DialogTitle>Add New County</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={states}
            getOptionLabel={(state) => state.state_name || ""}
            value={selectedCountyState || null}
            onChange={(e, selectedState) => setSelectedCountyState(selectedState || null)}
            isOptionEqualToValue={(option, value) => option.bond_state_id === value.bond_state_id}
            renderInput={(params) => <TextField {...params} label="Select State" margin="dense" variant="outlined" fullWidth />}
          />
          <TextField margin="dense" label="County Name" fullWidth variant="outlined" value={newCounty} onChange={(e) => setNewCounty(e.target.value)} />
          <Autocomplete
            options={carriers}
            getOptionLabel={(carrier) => carrier.name || ""}
            value={countyCarrier || null}
            onChange={(e, selectedCarrier) => setCountyCarrier(selectedCarrier || null)}
            isOptionEqualToValue={(option, value) => option.bond_carrier_id === value.bond_carrier_id}
            renderInput={(params) => <TextField {...params} label="Default Carrier" margin="dense" variant="outlined" fullWidth />}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCountyDialog} color="secondary">Cancel</Button>
          <Button onClick={handleSaveCounty} color="primary" disabled={submitting}>
            {submitting ? <CircularProgress size={20} /> : "Add County"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default StateList;
